import React, { useEffect, useState } from "react";
import MainLayouts from "../../layout/MainLayouts";

export const LEGAL_EMAIL = "info@e1xacademy.net";

/**
 * Shared shell for legal pages (Terms, Privacy): hero, sticky table of
 * contents with scroll-spy, and a content card.
 * `sections` is [{ id, title }]; the matching <LegalSection> ids live in children.
 */
export default function LegalLayout({ eyebrow, title, intro, lastUpdated, sections, children }) {
  const [activeSection, setActiveSection] = useState(sections[0]?.id);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length) setActiveSection(visible[0].target.id);
      },
      { rootMargin: "-120px 0px -60% 0px" }
    );
    sections.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [sections]);

  const scrollTo = (id) => {
    setActiveSection(id);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <MainLayouts>
      <div className="bg-slate-50 text-slate-800 min-h-screen">
        {/* Hero */}
        <header className="bg-[#001931] text-white pt-32 pb-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#d15424] mb-3">
              {eyebrow}
            </p>
            <h1 className="text-3xl sm:text-5xl font-bold tracking-tight">{title}</h1>
            <p className="mt-4 max-w-2xl text-slate-300 leading-relaxed">{intro}</p>
            <p className="mt-6 inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-xs text-slate-200">
              Last updated: {lastUpdated}
            </p>
          </div>
        </header>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8 pb-20">
          {/* Mobile table of contents */}
          <nav className="lg:hidden mb-6 bg-white rounded-2xl shadow-sm border border-slate-200/80 p-4">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-3">
              On this page
            </p>
            <div className="flex flex-wrap gap-2">
              {sections.map((section) => (
                <button
                  key={section.id}
                  onClick={() => scrollTo(section.id)}
                  className="px-3 py-1.5 text-xs rounded-full border border-slate-200 text-slate-600 hover:border-[#294a93] hover:text-[#294a93] transition-colors"
                >
                  {section.title}
                </button>
              ))}
            </div>
          </nav>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* Desktop table of contents */}
            <aside className="hidden lg:block lg:col-span-4">
              <nav className="sticky top-28 bg-white p-6 rounded-2xl shadow-sm border border-slate-200/80 space-y-1">
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-3">
                  On this page
                </p>
                {sections.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => scrollTo(section.id)}
                    className={`block w-full text-left px-3 py-2 text-sm rounded-lg border-l-2 transition-colors ${
                      activeSection === section.id
                        ? "border-[#d15424] bg-[#294a93]/5 text-[#294a93] font-semibold"
                        : "border-transparent text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                    }`}
                  >
                    {section.title}
                  </button>
                ))}
                <div className="mt-5 pt-5 border-t border-slate-100 text-sm text-slate-600">
                  Questions?{" "}
                  <a href={`mailto:${LEGAL_EMAIL}`} className="font-semibold text-[#d15424] hover:underline">
                    {LEGAL_EMAIL}
                  </a>
                </div>
              </nav>
            </aside>

            <main className="lg:col-span-8 space-y-10 bg-white p-6 sm:p-10 rounded-2xl shadow-sm border border-slate-200/80">
              {children}
            </main>
          </div>
        </div>
      </div>
    </MainLayouts>
  );
}

export function LegalSection({ id, title, children }) {
  return (
    <section id={id} className="space-y-4 scroll-mt-28">
      <h2 className="text-xl sm:text-2xl font-bold text-[#294a93] border-b border-slate-100 pb-3">
        {title}
      </h2>
      <div className="space-y-3 text-[15px] leading-relaxed text-slate-600">{children}</div>
    </section>
  );
}

export function ContactCard({ children }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-5 rounded-xl bg-[#294a93]/5 border border-[#294a93]/15">
      <div className="text-sm text-slate-600">{children}</div>
      <a
        href={`mailto:${LEGAL_EMAIL}`}
        className="inline-flex justify-center items-center rounded-lg bg-[#d15424] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#b84619] transition-colors whitespace-nowrap"
      >
        {LEGAL_EMAIL}
      </a>
    </div>
  );
}
